import { iInterface } from "@/services";
import * as priceService from "@/views/PriceSetting/services/priceService";

/**
 * 단가 설정 관련 상태 관리 스토어
 */
export default {
  namespaced: true,
  state: {
    projectId: null,
    projectName: "",
    priceGroupDetail: {},
    interfaceDetail: {},
    operateCostChanged: false,
    inspectCostChanged: false,
    isLoading: false,
    priceHistories: {
      count: 0,
      histories: [],
    },
    historyOptions: {
      limit: 15,
      order: "",
      page: 1,
      projectId: "",
      sort: "",
      groupId: null,
    },
    priceGroups: [],
  },

  mutations: {
    /**
     * 프로젝트 ID 설정
     */
    setProjectId(state, projectId) {
      state.projectId = projectId;
      state.historyOptions.projectId = projectId;
    },

    /**
     * 프로젝트명 설정
     */
    setProjectName(state, name) {
      state.projectName = name;
    },

    /**
     * 로딩 상태 설정
     */
    setLoading(state, isLoading) {
      state.isLoading = isLoading;
    },

    /**
     * 선택된 단가 그룹 설정
     */
    setPriceGroupDetail(state, group) {
      state.priceGroupDetail = group;
      if (group) {
        state.historyOptions.groupId = group.id;

        const index = state.priceGroups.findIndex(
          (item) => item.id === group.id
        );
        if (index !== -1) {
          state.priceGroups.splice(index, 1, group);
        }
      } else {
        state.historyOptions.groupId = null;
        state.priceGroupDetail = {};
      }
    },

    /**
     * 단가 그룹 목록 설정
     */
    setPriceGroups(state, groups) {
      state.priceGroups = groups;
    },

    /**
     * 인터페이스 상세 정보 설정
     * 객체 type별로 중복 된 것을 제거한다. (type 별로 단가를 주기 때문)
     */
    setInterfaceDetail(state, payload) {
      if (payload && payload.classData) {
        // type별로 중복 제거
        const uniqueTypes = new Set();
        state.interfaceDetail = {
          ...payload,
          classData: payload.classData.filter((item) => {
            if (uniqueTypes.has(item.type)) return false;
            uniqueTypes.add(item.type);
            return true;
          }),
        };
      } else {
        state.interfaceDetail = null;
      }
    },

    /**
     * 작업 단가 변경 여부 설정
     */
    setOperateCostChanged(state, value) {
      state.operateCostChanged = value;
    },

    /**
     * 검수 단가 변경 여부 설정
     */
    setInspectCostChanged(state, value) {
      state.inspectCostChanged = value;
    },

    /**
     * 단가 변경 내역 설정
     */
    setPriceHistories(state, histories) {
      state.priceHistories = histories;
    },

    /**
     * 단가 변경 내역에 추가
     */
    addPriceHistories(state, histories) {
      state.priceHistories.histories.push(...histories);
    },

    /**
     * 단가 변경 내역 옵션 설정
     */
    setHistoryOptions(state, options) {
      state.historyOptions = { ...state.historyOptions, ...options };
    },

    /**
     * 단가 변경 내역 페이지 증가
     */
    incrementHistoryPage(state) {
      state.historyOptions.page++;
    },

    /**
     * 단가 변경 내역 초기화
     */
    resetHistories(state) {
      state.priceHistories = { count: 0, histories: [] };
      state.historyOptions = {
        limit: 15,
        order: "",
        page: 1,
        projectId: state.projectId,
        sort: "",
        groupId: state.priceGroupDetail ? state.priceGroupDetail.id : null,
      };
    },
  },

  actions: {
    /**
     * 프로젝트 정보 로드
     */
    async loadProjectInfo({ commit }, projectId) {
      // 프로젝트 ID 설정
      commit("setProjectId", projectId);

      // 프로젝트명 조회
      const projectName = await priceService.getProjectName(projectId);
      if (projectName) {
        commit("setProjectName", projectName);
      }
    },

    /**
     * 단가 그룹 목록 조회
     */
    async loadPriceGroups({ state, commit }) {
      commit("setLoading", true);
      try {
        const groups = await priceService.getPriceGroups(state.projectId);
        commit("setPriceGroups", groups);
        return groups;
      } catch (error) {
        console.error("[PriceStore] 단가 그룹 목록 조회 중 오류 발생:", error);
        return [];
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * 단가 그룹 선택
     */
    async selectPriceGroup({ commit, dispatch }, group) {
      if (!group) {
        commit("setPriceGroupDetail", null);
        commit("setInterfaceDetail", null);
        commit("setOperateCostChanged", false);
        commit("setInspectCostChanged", false);
        commit("setLoading", false);
        return;
      }

      commit("setLoading", true);

      try {
        // 단가 그룹 정보 항상 최신 데이터로 갱신
        const groupDetail = await priceService.getPriceGroupDetail(group.id);
        if (groupDetail) {
          commit("setPriceGroupDetail", groupDetail);
        } else {
          commit("setPriceGroupDetail", group);
        }

        // 단가 변경 상태 초기화
        commit("setOperateCostChanged", false);
        commit("setInspectCostChanged", false);

        // 인터페이스 정보 조회
        await dispatch("fetchInterfaceDetail", group.interfaceId);
      } catch (error) {
        console.error("[PriceStore] 단가 그룹 선택 중 오류 발생:", error);
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * 단가 변경 내역 조회
     */
    async loadPriceHistories({ state, commit }) {
      commit("setLoading", true);
      try {
        const histories = await priceService.getPriceHistories(
          state.historyOptions
        );
        if (histories) {
          if (state.historyOptions.page === 1) {
            commit("setPriceHistories", histories);
          } else {
            commit("addPriceHistories", histories.histories);
          }
        }
      } catch (error) {
        commit("setLoading", false);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * 단가 설정
     */
    async setPriceInfo({ state, commit, dispatch }, payload) {
      commit("setLoading", true);
      try {
        const result = await priceService.setPriceInfo(payload);
        if (result) {
          // 단가 그룹 정보 다시 조회
          const groupDetail = await priceService.getPriceGroupDetail(
            state.priceGroupDetail.id
          );
          if (groupDetail) {
            commit("setPriceGroupDetail", groupDetail);

            // 인터페이스 정보도 함께 갱신
            await dispatch("fetchInterfaceDetail", groupDetail.interfaceId);
          }

          // 해당 작업 타입의 단가 변경 상태 초기화
          if (payload.workType === "OPERATE") {
            commit("setOperateCostChanged", false);
          } else if (payload.workType === "INSPECT") {
            commit("setInspectCostChanged", false);
          }

          // 단가 그룹 목록 다시 조회
          await dispatch("loadPriceGroups");

          return true;
        }
        return false;
      } catch (error) {
        console.error("[PriceStore] 단가 설정 중 오류 발생:", error);
        return false;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * 인터페이스 정보 조회
     */
    async fetchInterfaceDetail({ commit }, interfaceId) {
      const payload = { id: interfaceId };
      const res = await iInterface.getInterfaceDetail(payload);

      if (res && res.data && res.data.interface) {
        commit("setInterfaceDetail", res.data.interface);
      }
    },
  },

  getters: {
    priceGroups: (state) => state.priceGroups,

    selectedPriceGroupId: (state) => state.priceGroupDetail?.id || null,

    isLoading: (state) => state.isLoading,
  },
};
